import type { DimensionUnit } from './dimensions';
import type { FreightQuote } from './freight-api';
import type { FreightHandlingCharge, FreightInsurance, FreightReference } from './freight-requests';
import type { FulfillmentPlan } from './fulfillment-plan';
import type { FreightHazmatInfo, FreightPackagingType, FreightClass, LtlQuoteCharge } from './ltl';
import type { ResidentialIndicator } from './reference-data';
import type { WeightUnit } from './weight';

export type { FreightQuote, ResidentialIndicator };
export type FreightWeightUnit = 'Unknown' | WeightUnit;
export type FreightDimensionUnit = 'Unknown' | DimensionUnit;
export type HazmatIdType = 'UN' | 'NA';
export type PackingGroup = 'PGI' | 'PGII' | 'PGIII' | 'NoPackagingGroup';

export const FREIGHT_NOTIFICATION_TYPE = {
  QuoteReady: 'quote_ready',
  Exception: 'exception',
  Booked: 'booked',
  Delivered: 'delivered',
  InTransit: 'in_transit',
  OutForDelivery: 'out_for_delivery',
  PickupScheduled: 'pickup_scheduled',
  Voided: 'voided',
} as const;

export type FreightNotificationType = (typeof FREIGHT_NOTIFICATION_TYPE)[keyof typeof FREIGHT_NOTIFICATION_TYPE];

/** One recipient row: a set of addresses sharing one set of status events. */
export type FreightNotification = {
  emails: string[];
  types: FreightNotificationType[];
};

export type FreightAddress = {
  name: string | null;
  company: string | null;
  phone: string | null;
  /** not yet persisted server-side; will be non-null once the backend stores it at booking time. */
  email?: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
  residentialIndicator: ResidentialIndicator;
  verificationStatus: string | null;
  verificationMessage: string | null;
  verificationUtc: string | null;
  lockAddress: boolean;
  locationType: string | null;
};

export type FreightPickupDetails = {
  pickupDate: string | null;
  readyTime: string | null;
  closeTime: string | null;
  isSelfScheduled: boolean;
  locationType: string | null;
  pickupInstructions?: string | null;
  deliveryInstructions?: string | null;
  handlingInstructions?: string | null;
};

export type FreightCommodity = {
  commodityId?: number | null;
  /** The preset name, resolved server-side from the commodity preset id. Present on handling-unit-preset GET responses; absent on other endpoints. */
  name?: string | null;
  freightClass: FreightClass | null;
  description: string | null;
  packageType: string | null;
  quantity: number;
  weight: number | null;
  weightUnit: FreightWeightUnit;
  nmfcCode: string | null;
  isHazmat: boolean;
  hazmatInfo: FreightHazmatInfo | null;
};

/** A seller-saved, named commodity preset returned by GET /freight/commodity-preset. */
export type FreightFulfillmentPlanCommodityPreset = {
  commodityPresetId: number;
  name: string;
  packagingType: FreightPackagingType | null;
  commodity: FreightCommodity;
};

export type FreightCommodityPresetGridResponse = {
  currentPageCommodityPresetIds: number[];
  freightCommodityPresets: FreightFulfillmentPlanCommodityPreset[];
  page: number;
  pageSize: number;
  totalCount: number;
};

/** A seller-saved, named handling unit preset returned by GET /freight/handling-unit-preset. */
export type FreightFulfillmentPlanHandlingUnitPreset = {
  handlingUnitPresetId: number;
  name: string;
  packagingType: FreightPackagingType | null;
  quantity: number | null;
  weight: number | null;
  weightUnitOfMeasureId: number;
  length: number | null;
  width: number | null;
  height: number | null;
  dimensionUnitOfMeasureId: number;
  doNotStack: boolean;
  /** The commodity presets referenced by this preset. Persisted on Create/Update. */
  commodityPresetIds?: number[];
  /**
   * Commodity data for each referenced commodity preset, resolved server-side from
   * commodityPresetIds (same order). Output only — Create/Update read commodityPresetIds, not
   * this field. Included so a client can render full commodity fields without a separate round
   * trip per commodity preset ID.
   */
  commodities?: FreightCommodity[];
};

export const FreightCustomAttributeTypeId = {
  OrderNumber: 1,
  PONumber: 2,
  Custom: 3,
} as const;
export type FreightCustomAttributeTypeId = (typeof FreightCustomAttributeTypeId)[keyof typeof FreightCustomAttributeTypeId];

export type FreightCustomAttributeValueOption = {
  valueOptionId?: number | null;
  value: string;
  sortOrder: number;
  isActive: boolean;
};

export type FreightCustomAttributeDefinition = {
  definitionId: number;
  typeId: number;
  key: string;
  hasValueList: boolean;
  isActive: boolean;
  isBuiltIn: boolean;
  valueOptions: FreightCustomAttributeValueOption[];
};

export type FreightCustomAttributeAvailable = {
  typeId: number;
  definitionId: number | null;
  key: string;
  hasValueList: boolean;
  isBuiltIn: boolean;
  valueOptions: FreightCustomAttributeValueOption[];
};

export type FreightCustomAttributeGridRow = {
  definitionId: number | null;
  typeId: number;
  key: string;
  hasValueList: boolean;
  isActive: boolean;
  isBuiltIn: boolean;
  activeValueOptionCount: number;
};

export type FreightCustomAttributeGridResponse = {
  customAttributes: FreightCustomAttributeGridRow[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type FreightHandlingUnitPresetGridResponse = {
  currentPageHandlingUnitPresetIds: number[];
  freightHandlingUnitPresets: FreightFulfillmentPlanHandlingUnitPreset[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type FreightHandlingUnit = {
  handlingUnitId?: number | null;
  packagingType: FreightPackagingType | null;
  quantity: number;
  length: number;
  width: number;
  height: number;
  dimensionUnit: FreightDimensionUnit;
  weightUnit: FreightWeightUnit | null;
  isStackable: boolean;
  freightClass?: FreightClass | null;
  commodities: FreightCommodity[] | null;
};

export type FreightAccessorials = {
  isLiftgatePickup: boolean;
  isLiftgateDelivery: boolean;
  isInsidePickup: boolean;
  isInsideDelivery: boolean;
  isResidentialPickup: boolean;
  isResidentialDelivery: boolean;
  isNotifyBeforePickup: boolean;
  isAppointmentDelivery: boolean;
  isNotifyBeforeDelivery: boolean;
  isSortAndSegregate: boolean;
  isProtectionFromCold: boolean;
  isHoldAtTerminal: boolean;
  isConstructionSitePickup: boolean;
  isConstructionSiteDelivery: boolean;
  isCarrierTerminalPickup: boolean;
  isTradeshowPickup: boolean;
  isTradeshowDelivery: boolean;
  tradeshowPickupName: string | null;
  tradeshowPickupBoothNumber: string | null;
  tradeshowDeliveryName: string | null;
  tradeshowDeliveryBoothNumber: string | null;
  isGroceryConsolidationPickup: boolean;
  isGroceryConsolidationDelivery: boolean;
};

export type FreightDocument = {
  documentType: string;
  fileName: string;
  key: string;
  createDate: string;
};

export type FreightError = {
  code: string | null;
  message: string | null;
};

export type FreightTerminalAddress = {
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
};

export type FreightTerminalInfo = {
  terminalCode: string | null;
  address: FreightTerminalAddress | null;
  phoneNumber: string | null;
};

export type FreightTerminalDataResponse = {
  success: boolean;
  errors: FreightError[];
  origin: FreightTerminalInfo | null;
  destination: FreightTerminalInfo | null;
};

export type FreightShipment = {
  freightShipmentId: number;
  sellerFreightProviderId: number;
  shipFromWarehouseId: number | null;
  providerShipmentId: string | null;
  productTransactionId: string | null;
  bolNumber: string | null;
  proNumber: string | null;
  /** Carrier tracking URL for `proNumber`. Null until the first track event is received. */
  trackingUrl?: string | null;
  confirmationNumber: string | null;
  pickupTransactionId?: string | null;
  carrierName: string | null;
  carrierScac: string | null;
  serviceName: string | null;
  status: string;
  originAddress?: FreightAddress | null;
  destinationAddress: FreightAddress;
  originTerminalCode?: string | null;
  destinationTerminalCode?: string | null;
  shipDate: string | null;
  estimatedDeliveryDate: string | null;
  totalWeight: number | null;
  totalWeightUnit: FreightWeightUnit | null;
  totalCharges: number | null;
  transitDays: number | null;
  handlingUnits: FreightHandlingUnit[] | null;
  documents: FreightDocument[] | null;
  additionalServices: FreightAccessorials;
  insurance: FreightInsurance | null;
  pickupDetails: FreightPickupDetails | null;
  providerCode: string | null;
  offerId: string | null;
  createDate: string;
  quoteExpirationDate: string | null;
  /**
   * ISO 8601 UTC — when the shipment was voided. Null while the shipment is live.
   * Serialized from `FreightShipmentEntity.CancelledUtc`.
   */
  cancelledDate?: string | null;
  savedQuoteOptions: FreightQuote[] | null;
  selectedOfferId: string | null;
  fulfillmentPlanId?: string | null;
  references?: FreightReference[] | null;
  handlingCharge?: FreightHandlingCharge | null;
  /** Email notification recipients and the status events each is subscribed to. Null when the seller never configured any. */
  notifications?: FreightNotification[] | null;
  /** ISO 8601 UTC — timestamp of the most recent carrier scan. Null until first track event received. */
  lastTrackingEventUtc?: string | null;
  /** Fine-grained tracking status ID (dbo.FreightTrackingStatus). Null until first track event received. */
  freightTrackingStatusId?: number | null;
  /** Carrier-assigned pickup confirmation number (PRN). Null until the carrier assigns one. */
  pickupNumber?: string | null;
  /** Whether the selling channel was successfully notified that this shipment went out. */
  marketplaceNotified?: boolean;
  /** ISO 8601 UTC timestamp of the last marketplace notify attempt, success or failure alike. Null until a notify is attempted. */
  marketplaceNotifiedUtc?: string | null;
  /** Marketplace notify failure message. Set only when the last notify attempt failed; null on success or before the first attempt. */
  notifyErrorMessage?: string | null;
  charges?: LtlQuoteCharge[] | null;
};

export type FreightTrackingEventResponse = {
  eventDateTimeUtc: string | null;
  freightTrackingStatusId: number | null;
  carrierCode: string | null;
  description: string | null;
};

export type FreightTrackingDetail = {
  freightShipmentId: number | null;
  proNumber: string | null;
  bolNumber: string | null;
  status: string | null;
  trackingUrl: string | null;
  events: FreightTrackingEventResponse[] | null;
};

export type FreightTrackBulkLoad = {
  freightShipments: FreightShipment[] | null;
};

export type FreightTrackShipmentResponse = {
  bulkLoad: FreightTrackBulkLoad | null;
  trackingDetails: FreightTrackingDetail[] | null;
  success: boolean;
  errors: FreightError[] | null;
};

export type VoidFreightResult = {
  fulfillmentPlanId: number;
  success: boolean;
  errorMessage: string;
};

export type VoidFreightResponse = {
  results: VoidFreightResult[];
  allSucceeded: boolean;
};

export type SkippedFreightPreset = {
  id: number;
  reason: string;
};

export type BulkDeleteFreightPresetResponse = {
  deletedIds: number[];
  skipped: SkippedFreightPreset[];
};

export type FreightFulfillmentPlan = {
  fulfillmentPlanId: string;
  status: string;
  freightNumber: string | null;
  pickupDateUtc: string | null;
  sellerFreightProviderId: number | null;
  offerId: string | null;
  carrierName: string | null;
  carrierScac: string | null;
  serviceName: string | null;
  totalCharges: number | null;
  totalChargesCurrencyCode: string | null;
  transitDays: number | null;
  estimatedDeliveryDateUtc: string | null;
  quoteExpirationDateUtc: string | null;
  totalWeight: number | null;
  handlingUnits: FreightHandlingUnit[];
  settings: Record<string, string>;
};

export type FreightDensityValue = {
  value: number | null;
  unit: string;
};

export type FreightCommodityDensityResult = {
  density: FreightDensityValue;
  recommendedFreightClass: FreightClass | null;
};

export type FreightHandlingUnitDensityResult = {
  density: FreightDensityValue;
  commodities: FreightCommodityDensityResult[];
};

export type FreightCalculateDensityResponse = {
  isSuccess: boolean;
  errors: string[];
  averageDensity: FreightDensityValue;
  handlingUnitList: FreightHandlingUnitDensityResult[];
};

export type FreightFulfillmentPlanGridResponse = {
  currentPageFulfillmentPlanIds: string[];
  salesOrders: unknown[];
  fulfillmentPlans: FulfillmentPlan[];
  fulfillments: unknown[];
  fulfillmentPlanSets: unknown[];
  returns: unknown[];
  products: unknown[];
  pickBatches: unknown[];
  freightFulfillmentPlans: FreightFulfillmentPlan[];
  freightShipments: FreightShipment[];
  page: number;
  pageSize: number;
  totalCount: number;
};

/**
 * Response from `api/FreightShipmentGrid`. Mirrors {@link FreightFulfillmentPlanGridResponse}, but the page
 * is a list of freight shipment ids because the grid's rows are shipments rather than fulfillment plans.
 * The related order/plan/sales-order entities travel with it so the detail drawer opens without a second fetch.
 */
export type FreightShipmentGridResponse = {
  currentPageFreightShipmentIds: number[];
  salesOrders: unknown[];
  fulfillmentPlans: FulfillmentPlan[];
  fulfillments: unknown[];
  fulfillmentPlanSets: unknown[];
  returns: unknown[];
  products: unknown[];
  pickBatches: unknown[];
  freightShipments: FreightShipment[];
  page: number;
  pageSize: number;
  totalCount: number;
};

/** Response from `api/FreightGridStatusCounts`. */
export type FreightGridStatusCountsResponse = {
  /** Fulfillment plans with plan status "Quoted" and "Draft*/
  pendingQuotesCount: number;

  /** Shipment status*/
  bookedCount: number;
  pendingCount: number;
  inTransitCount: number;
  deliveredCount: number;
  exceptionCount: number;
  voidedCount: number;
};
